import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react"
import { ChevronDown, X } from "lucide-react"

import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"

/**
 * TikTok Ads Manager–style multi-select: chip field + dropdown with search.
 */

export interface SearchableMultiSelectOption {
  id: string
  name: string
}

interface Props {
  options: SearchableMultiSelectOption[]
  selected: string[]
  onToggle: (id: string) => void
  onClear?: () => void
  searchPlaceholder?: string
  emptyPlaceholder?: string
  emptyListText?: string
  noMatchesText?: string
  /** Expand the list in-flow below the field (avoids clipping inside modals). */
  inlineList?: boolean
}

export function SearchableMultiSelect({
  options,
  selected,
  onToggle,
  onClear,
  searchPlaceholder = "Search...",
  emptyPlaceholder = "Search...",
  emptyListText = "No options available",
  noMatchesText = "No matches found",
  inlineList = false,
}: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    function handler(e: globalThis.MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  // Clear the query whenever the dropdown is closed (the search input
  // only exists while open, so this is a one-shot reset per close).
  if (!open && search !== "") setSearch("")

  useEffect(() => {
    if (open) searchRef.current?.focus()
  }, [open])

  const selectedSet = useMemo(() => new Set(selected), [selected])

  const selectedOptions = useMemo(() => {
    const byId = new Map(options.map((o) => [o.id, o]))
    return selected
      .map((id) => byId.get(id) ?? { id, name: id })
      .filter(Boolean)
  }, [options, selected])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const sorted = [...options].sort((a, b) => a.name.localeCompare(b.name))
    if (!q) return sorted
    return sorted.filter((o) => o.name.toLowerCase().includes(q))
  }, [options, search])

  function clearAll(e: MouseEvent) {
    e.stopPropagation()
    if (onClear) {
      onClear()
      return
    }
    ;[...selected].forEach((id) => onToggle(id))
  }

  function onTriggerKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      setOpen((v) => !v)
    } else if (e.key === "Escape") {
      setOpen(false)
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div
        role="combobox"
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen(true)}
        onKeyDown={onTriggerKeyDown}
        className="flex min-h-10 w-full cursor-text items-center gap-2 rounded-md border border-[#5a5a5a] bg-[#1f1f1f] px-2.5 py-1.5 text-left hover:border-[#7a7a7a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {selectedOptions.length === 0 ? (
            <span className="text-sm text-white/50">{emptyPlaceholder}</span>
          ) : (
            selectedOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                aria-label={`Remove ${opt.name}`}
                className="inline-flex max-w-full items-center gap-1 rounded border border-[#5a5a5a] bg-[#3a3a3a] px-2 py-0.5 text-xs text-white hover:border-[#7a7a7a] hover:bg-[#454545]"
                onClick={(e) => {
                  e.stopPropagation()
                  onToggle(opt.id)
                }}
              >
                <span className="truncate">{opt.name}</span>
                <X className="h-3 w-3 shrink-0 text-white/70" aria-hidden />
              </button>
            ))
          )}
        </div>
        {selectedOptions.length > 0 ? (
          <button
            type="button"
            aria-label="Clear all"
            className="shrink-0 rounded-full p-0.5 text-white/50 hover:bg-[#3a3a3a] hover:text-white"
            onClick={clearAll}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
        <button
          type="button"
          aria-label={open ? "Close list" : "Open list"}
          className="shrink-0 text-white/50 hover:text-white"
          onClick={(e) => {
            e.stopPropagation()
            setOpen((v) => !v)
          }}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open ? (
        <div
          className={
            inlineList
              ? "relative z-10 mt-1 w-full overflow-hidden rounded-md border border-[#5a5a5a] bg-[#262626]"
              : "absolute z-30 mt-1 w-full overflow-hidden rounded-md border border-[#5a5a5a] bg-[#262626] shadow-lg"
          }
        >
          <div className="border-b border-[#4a4a4a] p-2">
            <Input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8"
            />
          </div>
          <div
            className={
              inlineList
                ? "max-h-48 overflow-y-auto p-1"
                : "max-h-56 overflow-y-auto p-1"
            }
            role="listbox"
          >
            {filtered.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-white/50">
                {options.length === 0 ? emptyListText : noMatchesText}
              </p>
            ) : (
              filtered.map((opt) => {
                const checked = selectedSet.has(opt.id)
                return (
                  <label
                    key={opt.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-white hover:bg-[#3a3a3a]"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => onToggle(opt.id)}
                    />
                    <span>{opt.name}</span>
                  </label>
                )
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
